import React, { createContext, useContext, useEffect, useMemo, useState } from "react";

import {
  AppEnvironment,
  CHAIN_ID,
  Decimal18,
  GANACHE_PORT,
  initializeConstants,
  SupportedCollaterals
} from "@astrid-dao/lib-base";
import {
  BlockPolledAstridDaoStore,
  EthersAstridDao,
  EthersAstridDaoWithStore,
  getDeploymentConstantsFor,
  _connectByChainId
} from "@astrid-dao/lib-ethers";
import { Provider } from "@ethersproject/abstract-provider";
import { FallbackProvider } from "@ethersproject/providers";
import { Signer } from "ethers";
import { useAccount, useDisconnect, useNetwork, useProvider, useSigner } from "wagmi";

import { BaseProp } from "../@types/base-types";
import {
  isLocalDevelopmentMode,
  collateralTypesForLocal,
  astridDaoFrontendConfigProxy,
  stablecoinCollateralTypes,
  SupportedNetworks,
  collateralTypesWithInfiniteAllowance,
  FrontendConfig,
  getShibuyaChainIDMaybeLocal
} from "../config";
import { connectToContracts } from "../rpc-contract-service/contracts";
import ContractsJson from "../rpc-contract-service/contracts.json";
// import { isBatchedProvider } from "./BatchedProvider";
import { BatchedProvider } from "./BatchedProviderV2";
import { isWebSocketAugmentedProvider } from "./WebSocketAugmentedProvider";

export type CollateralConfigJson = {
  name: string;
  address: string;
  contracts: {
    activePool: string;
    borrowerOperations: string;
    collSurplusPool: string;
    defaultPool: string;
    gasPool: string;
    hintHelpers: string;
    priceFeed: string;
    sortedVaults: string;
    stabilityPool: string;
    vaultManager: string;
    atidStaking: string;
    multiVaultGetter: string;
    multiStakeGetter: string;
  };
};

export type BaseContractsConfigJson = {
  communityIssuance: string;
  baiToken: string;
  xcBaiToken: string;
  xcBaiWrapper: string;
  atidToken: string;
  govToken: string;
  WASTR: string;
};

export type PublicSaleContractsConfigJson = {
  crowdsale: string;
  crowdsaleRecords: string;
};

export type AirdropContractConfigJson = {
  name: string;
  contract: string;
};

export type ContractsConfigJson = {
  baseContracts: BaseContractsConfigJson;
  publicSaleContracts: PublicSaleContractsConfigJson;
  airdropContracts: AirdropContractConfigJson[];
  collaterals: CollateralConfigJson[];
};

type AstridDaoContextValue = {
  account: Readonly<string>;
  astridDao: EthersAstridDaoWithStore<BlockPolledAstridDaoStore>;
  collateralDecimalPrecision: Readonly<number>;
  contractsConfig: ContractsConfigJson;
  criticalCollateralRatio: Decimal18;
  deactivate: () => void;
  filteredCollateralTypes: Readonly<string[]>;
  frontendConfig: FrontendConfig;
  isInfiniteAllowanceMode: Readonly<boolean>;
  isStablecoinMode: Readonly<boolean>;
  isTestnet: boolean;
  minimumCollateralRatio: Decimal18;
  provider: Provider;
  setFrontendConfig: React.Dispatch<React.SetStateAction<FrontendConfig>>;
  signer: Signer;
};

const AstridDaoContext = createContext<AstridDaoContextValue | undefined>(undefined);

type AstridDaoProviderProps = {
  loader?: React.ReactNode;
  environmentInitFailedFallback?: React.ReactNode;
} & BaseProp;

/**
 * See more here https://docs.astar.network/integration/network-details
 */
const webSocketChainIdMap: Readonly<Record<string, string>> = {
  // [CHAIN_ID.astar]: "wss://fake",
  // [CHAIN_ID.astar]: "wss://rpc.pinknode.io/astar/a96aaaad-7dc4-4345-8a6c-7a4d3452fb4c",
  // [CHAIN_ID.astar]: "wss://astar.blastapi.io/2c38533d-fa85-4099-9db7-a018494d6e24",
  [CHAIN_ID.astar]: "wss://astar.api.onfinality.io/ws?apikey=55873d07-deea-45e0-ac8f-eade414bde74",
  [CHAIN_ID.shibuya]: "wss://fake",
  [GANACHE_PORT]: `ws://${window.location.hostname}:${GANACHE_PORT}`,
  [CHAIN_ID.dev]: `ws://${window.location.hostname}:8546`
};

const chainIdNetworkMap: Record<number, SupportedNetworks> = {
  [CHAIN_ID.shibuya]: "shibuya",
  [CHAIN_ID.astar]: "astar",
  [CHAIN_ID.dev]: "shibuya"
};

export const AstridDaoProvider: React.FC<AstridDaoProviderProps> = ({
  children,
  loader,
  environmentInitFailedFallback
}) => {
  const { address: account } = useAccount();
  const provider = useProvider<FallbackProvider>();
  const { chain } = useNetwork();
  const chainIdFromWeb3React = chain?.id;
  const { disconnect } = useDisconnect();
  const deactivate = () => {
    disconnect();
  };
  const signer = useSigner();

  const [frontendConfig, setFrontendConfig] = useState(astridDaoFrontendConfigProxy);

  const collateralConstantsFromDeployment = getDeploymentConstantsFor(
    frontendConfig.chainId,
    frontendConfig.collateralName
  );

  const isInfiniteAllowanceMode = collateralTypesWithInfiniteAllowance.includes(
    astridDaoFrontendConfigProxy.collateralName
  );

  const networkName: SupportedNetworks =
    chainIdFromWeb3React === CHAIN_ID.astar ? "astar" : "shibuya";

  const {
    CRITICAL_COLLATERAL_RATIO: criticalCollateralRatio,
    MINIMUM_COLLATERAL_RATIO: minimumCollateralRatio,
    COLLATERAL_DECIMAL_PRECISION: collateralDecimalPrecision,
    errors: envInitializationErrors
  } = initializeConstants(
    `${networkName}-${frontendConfig.collateralName}` as AppEnvironment,
    collateralConstantsFromDeployment
  );

  const isStablecoinMode = stablecoinCollateralTypes.includes(
    astridDaoFrontendConfigProxy.collateralName
  );

  const wereEnvVariablesSet = Boolean(
    process.env.REACT_APP_ENV && frontendConfig.chainId && frontendConfig.collateralName
  );
  const wasMcrSet = !minimumCollateralRatio?.isZero;
  const wasCcrSet = !criticalCollateralRatio?.isZero;
  const shouldShowEnvInitFailed = !wereEnvVariablesSet || !wasMcrSet || !wasCcrSet;

  /** The list of supported collateral types except for the runtime collateral instance */
  const filteredCollateralTypes: Readonly<string[]> = (
    frontendConfig.chainId === CHAIN_ID.astar
      ? ["WASTR", "WBTC", "WETH", "DOT", "BUSD", "DAI", "ceUSDC", "ceUSDT"]
      : ["WASTR", "DOT", "ceUSDC", "ceUSDT", "WBTC"]
  ).filter(collateral => collateral !== frontendConfig.collateralName);
  const RPC_NETWORK = chainIdNetworkMap[frontendConfig.chainId];
  const contractsConfig: ContractsConfigJson = ContractsJson.network[RPC_NETWORK];
  const isTestnet = chainIdFromWeb3React === getShibuyaChainIDMaybeLocal();

  const connection = useMemo(() => {
    if (provider && account && chainIdFromWeb3React) {
      const collateralName = frontendConfig.collateralName;

      try {
        return _connectByChainId(
          new BatchedProvider(provider, chainIdFromWeb3React),
          // provider.getSigner(account),
          // @ts-ignore
          signer.data,
          chainIdFromWeb3React,
          collateralName,
          {
            userAddress: account,
            useStore: "blockPolled"
          }
        );
      } catch (error) {
        console.error("Failed to connect by chain id", error);
      }
    }
  }, [provider, account, chainIdFromWeb3React, frontendConfig.collateralName, signer.data]);

  useEffect(() => {
    if (connection && chainIdFromWeb3React) {
      const { provider, chainId } = connection;

      try {
        connectToContracts(isTestnet, contractsConfig, signer.data);
      } catch (error) {
        console.error("Failed to connect to contracts called via RPC", error);
      }

      if (isWebSocketAugmentedProvider(provider)) {
        if (isLocalDevelopmentMode) {
          switch (chainId) {
            case CHAIN_ID.dev:
              console.log("Connected to dev-chain");
              break;
            case getShibuyaChainIDMaybeLocal():
              console.log("Connected to Shibuya Testnet");
              break;
            case CHAIN_ID.astar:
              console.log("Connected to Astar Network");
              break;
          }
        }

        const webSocketChainId = chainIdFromWeb3React ?? chainId;
        const webSocketUrl =
          chain?.rpcUrls?.public?.webSocket?.at?.(0) ?? webSocketChainIdMap[webSocketChainId];

        provider.openWebSocket(webSocketUrl, webSocketChainId);

        return () => {
          provider.closeWebSocket();
        };
      }
    }
  }, [
    connection,
    chainIdFromWeb3React,
    frontendConfig,
    contractsConfig,
    isTestnet,
    signer.data,
    chain?.rpcUrls?.public?.webSocket
  ]);

  // Ensure that we don't let users use the app if environment initialization failed
  if (shouldShowEnvInitFailed && !isLocalDevelopmentMode) {
    console.error("Environment failed to initialize! \n", envInitializationErrors);

    return <>{environmentInitFailedFallback}</>;
  }

  if (!provider || !account || !chainIdFromWeb3React || !connection || !signer.data) {
    return <>{loader}</>;
  }

  const astridDao = EthersAstridDao._from(connection);
  astridDao.store.logging = false;

  // const signer = provider.getSigner(account);

  return (
    <AstridDaoContext.Provider
      value={{
        account,
        astridDao,
        collateralDecimalPrecision,
        contractsConfig,
        criticalCollateralRatio,
        deactivate,
        filteredCollateralTypes,
        frontendConfig,
        isInfiniteAllowanceMode,
        isStablecoinMode,
        isTestnet,
        minimumCollateralRatio,
        provider,
        setFrontendConfig,
        // @ts-ignore
        signer: signer.data
      }}
    >
      {children}
    </AstridDaoContext.Provider>
  );
};

export const useAstridDao = () => {
  const astridDaoContext = useContext(AstridDaoContext);

  if (!astridDaoContext) {
    throw new Error("You must provide an AstridDaoContext via AstridDaoProvider");
  }

  return astridDaoContext;
};
